/* istanbul ignore file */
/* It's not possible to fill VGS fields in Cypress */
import { useFormikContext } from 'formik'
import { useCallback, useEffect } from 'react'
import { useMatch } from 'react-router-dom'

import { Response } from '@api/reservation/checkCard'
import { useCheckCard as useMutation } from '@loaders/checkCard'
import { CheckoutFormData } from '@pages/Checkout/hooks/useInitialFormValues'
import { useCheckPaymentState } from '@pages/Checkout/Payment/CheckPayment/useCheckPaymentState'
import { useSettings } from '@queries/settings'
import { useParams } from '@stores/params'

type CheckSuccessCallback = (response: Response) => void

const FIELD_NAME = 'checkCardState'

type CardDetails = {
  cardNumber: string
  cardExpiry?: string
  expiryMonth?: string
  expiryYear?: string
  cvv: string
  cardHolderName: string
  cardType: string
}

export const useCheckPayment = (onSuccess?: CheckSuccessCallback) => {
  const [{ marketingCarrierCode, retailerPartnerNumber }] = useParams()
  const [{ paymentProvidersMethods }] = useSettings()
  const {
    values: { reservationData },
    setFieldValue,
  } = useFormikContext<CheckoutFormData>()

  const isReservation = useMatch('reservation')
  const enablePaymentCheck = isReservation === null && !!paymentProvidersMethods.creditCard?.options?.checkDetails

  const { setCheckStatus, setCardError, isPaymentChecked, cardError } = useCheckPaymentState(
    FIELD_NAME,
    enablePaymentCheck,
  )

  useEffect(() => {
    if (enablePaymentCheck) {
      setCheckStatus(false)
    }
  }, [enablePaymentCheck, setCheckStatus])

  const {
    mutate,
    data: checkResult,
    error,
  } = useMutation({
    onSuccess: response => {
      const { totalPrice, fees } = response

      setCheckStatus(true)
      setCardError(undefined)

      onSuccess?.(response)

      setFieldValue('price', { fractional: totalPrice.fractional, currency: totalPrice.currency })

      setFieldValue('reservationData', {
        ...reservationData,
        fees: fees,
      })
      setFieldValue('isPaymentDetailsLoading', false)
    },
    onError: error => {
      setCheckStatus(false)
      setCardError(error.message)

      setFieldValue('isPaymentDetailsLoading', false)
    },
  })

  const handleCheck = useCallback(
    (paymentMethod: PaymentMethodType, cardDetails?: CardDetails) => {
      if (!reservationData?.id) return

      setFieldValue('isPaymentDetailsLoading', true)

      mutate({
        reservation_id: reservationData.id,
        marketing_carrier_code: marketingCarrierCode as string,
        retailer_partner_number: retailerPartnerNumber.toString(),
        payment_method: paymentMethod,
        ...(cardDetails && {
          cardDetails: {
            card_number: cardDetails.cardNumber,
            card_expiry: cardDetails?.cardExpiry,
            card_expiry_year: cardDetails?.expiryYear,
            card_expiry_month: cardDetails?.expiryMonth,
            security_code: cardDetails?.cvv,
            card_holder_name: cardDetails?.cardHolderName,
            card_type: cardDetails.cardType,
          },
        }),
      })
    },
    [mutate, reservationData?.id, marketingCarrierCode, retailerPartnerNumber, setFieldValue],
  )

  return {
    handleCheck,
    checkResult,
    error,
    fieldName: FIELD_NAME,
    isEnabled: enablePaymentCheck,
    isPaymentChecked,
    cardError,
    setCardError,
    setCheckStatus,
  }
}
